import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { COMPANY_NAME } from "@/lib/brand";
import { Printer } from "lucide-react";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n) =>
  Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 });

export default function OrderCostSheet() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cs, orders, custs] = await Promise.all([
          api.get(`/orders/${id}/cost-sheet`),
          api.get("/orders"),
          api.get("/customers"),
        ]);
        if (cancelled) return;
        setData(cs.data);
        const o = orders.data.find((x) => x.id === id);
        setOrder(o || null);
        if (o) setCustomer(custs.data.find((c) => c.id === o.customer_id) || null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load cost sheet");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error) {
    return (
      <div className="p-8 font-mono text-xs text-red-600" data-testid="cost-sheet-error">
        {error}
      </div>
    );
  }
  if (!data || !order) {
    return (
      <div className="p-8 font-mono text-xs text-zinc-500" data-testid="cost-sheet-loading">
        LOADING...
      </div>
    );
  }

  const { per_carton, by_material, by_bucket, totals } = data;

  return (
    <div className="min-h-screen bg-zinc-100 print:bg-white">
      {/* Print bar (hidden in actual print) */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-zinc-200 px-4 py-2 flex items-center justify-between">
        <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-600">
          <a
            href="/orders"
            data-testid="cost-sheet-back"
            className="text-zinc-500 hover:text-orange-700 mr-3"
          >
            ← Orders
          </a>
          Order Cost Sheet · <span className="text-orange-700 font-semibold">{order.order_no}</span>
        </div>
        <button
          data-testid="print-cost-sheet"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1 text-[11px] uppercase font-mono border border-orange-300 bg-orange-50 text-orange-800 px-3 py-1.5 hover:bg-orange-100"
        >
          <Printer size={12} /> Print
        </button>
      </div>

      <div className="max-w-5xl mx-auto bg-white p-8 my-4 print:my-0 print:p-6 shadow print:shadow-none">
        {/* Header */}
        <div className="border-b-2 border-zinc-900 pb-3 mb-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                {COMPANY_NAME}
              </div>
              <h1 className="text-2xl font-bold text-zinc-950">Order Cost Sheet</h1>
              <div className="text-[11px] font-mono text-zinc-600 mt-1">
                FIFO lot pricing · GST inclusive
              </div>
            </div>
            <div className="text-right text-[11px] font-mono text-zinc-700">
              <div>
                <span className="text-zinc-500">Order No: </span>
                <span className="text-orange-700 font-semibold">{order.order_no}</span>
              </div>
              <div>
                <span className="text-zinc-500">Status: </span>
                <span className="uppercase">{order.status}</span>
              </div>
              {customer && (
                <div>
                  <span className="text-zinc-500">Customer: </span>
                  <span className="text-zinc-900">{customer.name}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Tile label="Total Cartons" value={totals.total_cartons} testid="ttl-cartons" />
          <Tile label="Net Weight (kg)" value={fmtQty(totals.total_net_kg)} testid="ttl-net-kg" />
          <Tile label="Total Cost (₹, incl GST)" value={fmt(totals.total_cost_incl_gst)} testid="ttl-cost" highlight />
          <Tile label="Cost / kg (₹)" value={fmt(totals.cost_per_kg)} testid="ttl-cost-per-kg" />
        </div>

        {totals.total_cartons === 0 && (
          <div className="border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 font-mono">
            No cartons are assigned or dispatched against this order yet. Assign stock to see the cost sheet populate.
          </div>
        )}

        {/* Missing-pricing warning (the most common reason a carton looks
            cheaper than expected — one of its recipe materials has no priced
            lot yet, so that component contributes ₹0). */}
        {data.missing_pricing && data.missing_pricing.length > 0 && (
          <div className="bg-amber-50 border border-amber-400 px-4 py-3 mb-5" data-testid="missing-pricing-warning">
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-800 font-semibold mb-1">
              ⚠ Some components are unpriced
            </div>
            <div className="text-xs text-zinc-700 mb-2">
              The cost below is missing ₹ for the following raw materials because no priced lot has been
              received for them yet. Receive a lot via Inventory → Raw Material → Receive, then refresh —
              the cost will fill in automatically (FIFO).
            </div>
            <table className="w-full text-[11px] border border-amber-200 bg-white">
              <thead className="bg-amber-100 text-[9px] font-mono uppercase">
                <tr>
                  <th className="px-2 py-1 text-left">Material</th>
                  <th className="px-2 py-1 text-right">Unpriced Qty</th>
                  <th className="px-2 py-1 text-right">Cartons affected</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {data.missing_pricing.map((m) => (
                  <tr key={m.material} className="border-t border-amber-100">
                    <td className="px-2 py-1 text-zinc-900">{m.material}</td>
                    <td className="px-2 py-1 text-right">{Number(m.missing_qty).toFixed(3)} {m.unit}</td>
                    <td className="px-2 py-1 text-right">{m.carton_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* By Material */}
        {by_material.length > 0 && (
          <Section title="Raw Material Usage (FIFO)">
            <table className="w-full text-xs border border-zinc-300" data-testid="cost-by-material">
              <thead className="bg-zinc-100 text-[10px] uppercase font-mono tracking-wider text-zinc-600">
                <tr>
                  <th className="px-2 py-1.5 text-left border-b border-zinc-300">Material</th>
                  <th className="px-2 py-1.5 text-left border-b border-zinc-300">Role</th>
                  <th className="px-2 py-1.5 text-right border-b border-zinc-300">Qty Used</th>
                  <th className="px-2 py-1.5 text-right border-b border-zinc-300">Unit</th>
                  <th className="px-2 py-1.5 text-right border-b border-zinc-300">Cost (₹ incl GST)</th>
                </tr>
              </thead>
              <tbody>
                {by_material.map((m) => (
                  <tr key={m.material} className="border-b border-zinc-200">
                    <td className="px-2 py-1.5 text-zinc-900">{m.material}</td>
                    <td className="px-2 py-1.5 text-zinc-600">{m.bucket}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtQty(m.qty)}</td>
                    <td className="px-2 py-1.5 text-right text-zinc-500">{m.unit}</td>
                    <td className="px-2 py-1.5 text-right font-mono">₹ {fmt(m.cost)}</td>
                  </tr>
                ))}
                <tr className="bg-orange-50 font-semibold">
                  <td className="px-2 py-1.5" colSpan={4}>
                    Grand Total
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-orange-800">
                    ₹ {fmt(totals.total_cost_incl_gst)}
                  </td>
                </tr>
              </tbody>
            </table>
          </Section>
        )}

        {/* By Bucket (recipe role) */}
        {by_bucket.length > 0 && (
          <Section title="Cost by Recipe Role">
            <table className="w-full text-xs border border-zinc-300" data-testid="cost-by-bucket">
              <thead className="bg-zinc-100 text-[10px] uppercase font-mono tracking-wider text-zinc-600">
                <tr>
                  <th className="px-2 py-1.5 text-left border-b border-zinc-300">Role</th>
                  <th className="px-2 py-1.5 text-left border-b border-zinc-300">Total Qty</th>
                  <th className="px-2 py-1.5 text-right border-b border-zinc-300">Cost (₹ incl GST)</th>
                  <th className="px-2 py-1.5 text-right border-b border-zinc-300">% of Order</th>
                </tr>
              </thead>
              <tbody>
                {by_bucket.map((b) => {
                  const qtyStr = Object.entries(b.qty_by_unit || {})
                    .map(([u, q]) => `${fmtQty(q)} ${u}`)
                    .join(", ");
                  const pct = totals.total_cost_incl_gst
                    ? ((b.cost / totals.total_cost_incl_gst) * 100).toFixed(2)
                    : "0.00";
                  return (
                    <tr key={b.bucket} className="border-b border-zinc-200">
                      <td className="px-2 py-1.5 text-zinc-900">{b.bucket}</td>
                      <td className="px-2 py-1.5 text-zinc-700 font-mono">{qtyStr}</td>
                      <td className="px-2 py-1.5 text-right font-mono">₹ {fmt(b.cost)}</td>
                      <td className="px-2 py-1.5 text-right text-zinc-600 font-mono">{pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Section>
        )}

        {/* Per Carton */}
        {per_carton.length > 0 && (
          <Section title="Per-Carton Breakdown">
            <table className="w-full text-xs border border-zinc-300" data-testid="cost-per-carton">
              <thead className="bg-zinc-100 text-[10px] uppercase font-mono tracking-wider text-zinc-600">
                <tr>
                  <th className="px-2 py-1.5 text-left border-b border-zinc-300">Box No</th>
                  <th className="px-2 py-1.5 text-left border-b border-zinc-300">SKU</th>
                  <th className="px-2 py-1.5 text-right border-b border-zinc-300">Net kg</th>
                  <th className="px-2 py-1.5 text-left border-b border-zinc-300">Components</th>
                  <th className="px-2 py-1.5 text-right border-b border-zinc-300">Cost (₹)</th>
                  <th className="px-2 py-1.5 text-right border-b border-zinc-300">₹/kg</th>
                </tr>
              </thead>
              <tbody>
                {per_carton.map((c) => (
                  <tr key={c.box_no} className="border-b border-zinc-200 align-top">
                    <td className="px-2 py-1.5 font-mono text-zinc-900">{c.box_no}</td>
                    <td className="px-2 py-1.5 text-zinc-700">{c.sku}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtQty(c.net_weight_kg)}</td>
                    <td className="px-2 py-1.5 text-[10px] text-zinc-700 leading-snug">
                      {Object.entries(c.breakdown || {}).map(([name, b]) => (
                        <div key={name}>
                          <span className="text-zinc-900">{name}</span>{" "}
                          <span className="text-zinc-500">
                            ({fmtQty(b.qty)} {b.unit})
                          </span>{" "}
                          <span className="font-mono text-zinc-600">₹{fmt(b.cost)}</span>
                        </div>
                      ))}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">₹ {fmt(c.total_cost_incl_gst)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-zinc-600">
                      ₹ {fmt(c.cost_per_kg)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-orange-50 font-semibold">
                  <td className="px-2 py-1.5" colSpan={2}>
                    Grand Total ({totals.total_cartons} cartons)
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmtQty(totals.total_net_kg)}</td>
                  <td />
                  <td className="px-2 py-1.5 text-right font-mono text-orange-800">
                    ₹ {fmt(totals.total_cost_incl_gst)}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-orange-800">
                    ₹ {fmt(totals.cost_per_kg)}
                  </td>
                </tr>
              </tbody>
            </table>
          </Section>
        )}

        <div className="text-[10px] font-mono text-zinc-400 mt-6 pt-3 border-t border-zinc-200">
          Costs use FIFO lot prices captured at the time of carton production (inclusive of GST).
          Cartons that were produced before lot-tracking was enabled may show ₹0 — those are legacy
          consumption entries with no recorded lot price.
        </div>
      </div>

      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
}

function Tile({ label, value, testid, highlight }) {
  return (
    <div
      data-testid={testid}
      className={`border ${
        highlight ? "border-orange-300 bg-orange-50" : "border-zinc-300 bg-zinc-50"
      } px-3 py-2`}
    >
      <div className="text-[9px] uppercase font-mono tracking-widest text-zinc-500">{label}</div>
      <div
        className={`text-lg font-bold mt-0.5 ${
          highlight ? "text-orange-800" : "text-zinc-900"
        } font-mono`}
      >
        {value}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5">
      <div className="text-[11px] uppercase font-mono tracking-widest text-zinc-700 mb-2 border-l-2 border-orange-500 pl-2">
        {title}
      </div>
      {children}
    </div>
  );
}
